import React, { useState, useEffect } from 'react';
import { useAppContext } from '../contexts/AppContext.tsx';
import { format, addMonths, subMonths } from 'date-fns';
import { ko } from 'date-fns/locale';

type SortType = 'numeric' | 'alphabetic' | 'korean';

const TeamsPage: React.FC = () => {
  const { employees, teams, budgetData, setTeams, setBudgetData, saveTeamData, isLoading } = useAppContext();
  const [teamName, setTeamName] = useState('');
  const [teamSortType, setTeamSortType] = useState<SortType>('numeric');
  const [isAddingTeam, setIsAddingTeam] = useState(false);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [currentMonthData, setCurrentMonthData] = useState<{
    teamTargetSales: Record<string, number>;
  }>({
    teamTargetSales: {}
  });
  
  // 날짜 네비게이션
  const handlePreviousMonth = () => {
    const newDate = subMonths(currentDate, 1);
    setCurrentDate(newDate);
  };

  const handleNextMonth = () => {
    const newDate = addMonths(currentDate, 1);
    setCurrentDate(newDate);
  };
  
  // 팀 이름 배열 생성 (안전하게 처리)
  const [localTeams, setLocalTeams] = useState<string[]>([]);
  useEffect(() => {
    if (teams && Array.isArray(teams)) {
      // TeamData[] 배열에서 name 속성만 추출하여 string[] 배열 생성
      const teamNames = teams
        .filter(team => team && typeof team === 'object' && team.name)
        .map(team => team.name);
      setLocalTeams(teamNames);
    } else {
      setLocalTeams([]); // 잘못된 형식이면 빈 배열로 초기화
    }
  }, [teams]);
  
  // 선택된 월에 해당하는 데이터 로드
  useEffect(() => {
    const currentYearMonth = format(currentDate, 'yyyy-MM');
    
    // 선택한 월의 데이터가 있으면 해당 데이터 사용, 없으면 빈 데이터로 시작
    if (budgetData.monthlyData && budgetData.monthlyData[currentYearMonth]) {
      setCurrentMonthData({
        teamTargetSales: budgetData.monthlyData[currentYearMonth].teamTargetSales || {}
      });
    } else {
      // 해당 월 데이터가 없으면 빈 데이터로 시작
      setCurrentMonthData({
        teamTargetSales: {}
      });
    }
  }, [currentDate, budgetData]);

  // 팀 정렬 함수
  const getSortedTeams = (teamsToSort: string[], sortType: SortType): string[] => {
    // 원본 배열 복사
    const sorted = [...teamsToSort];
    
    switch(sortType) {
      case 'numeric':
        // 숫자가 포함된 팀 이름을 숫자 기준으로 정렬
        return sorted.sort((a, b) => {
          // 팀 이름에서 숫자 추출
          const numA = parseInt(a.replace(/[^0-9]/g, '')) || 0;
          const numB = parseInt(b.replace(/[^0-9]/g, '')) || 0;
          return numA - numB;
        });
      
      case 'alphabetic':
        // 알파벳 순서로 정렬 (대소문자 구분 없이)
        return sorted.sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }));
      
      case 'korean':
        // 한글 가나다 순서로 정렬
        return sorted.sort((a, b) => a.localeCompare(b, 'ko'));
      
      default:
        return sorted;
    }
  };

  // 팀 추가 함수
  const handleAddTeam = () => {
    const trimmedName = teamName.trim();
    
    if (!trimmedName) {
      alert('팀 이름을 입력해주세요.');
      return;
    }
    
    // 대소문자 구분 없이 중복 검사 (정규화된 팀 이름으로 비교)
    const normalizedTeamName = trimmedName.toLowerCase();
    const teamExists = localTeams.some(t => t.toLowerCase() === normalizedTeamName);
    
    if (teamExists) {
      alert('이미 존재하는 팀 이름입니다.');
      return;
    }
    
    // 로컬 상태에 팀 추가
    setLocalTeams(prev => [...prev, trimmedName]);
    
    // AppContext에 팀 추가 (팀 데이터 객체 생성)
    const newTeamId = `team_${Date.now()}`;
    const newTeam = {
      id: newTeamId,
      name: trimmedName,
      targetSales: 0
    };
    setTeams(prev => Array.isArray(prev) ? [...prev, newTeam] : [newTeam]);
    
    // 팀 목표매출, 인센티브 비율, 인건비율 초기화
    setBudgetData(prev => ({
      ...prev,
      teamTargetSales: {
        ...prev.teamTargetSales,
        [trimmedName]: 0
      },
      teamIncentiveRatios: {
        ...prev.teamIncentiveRatios,
        [trimmedName]: {
          performance: prev.incentiveRatios.performance,
          team: prev.incentiveRatios.team,
          member: prev.incentiveRatios.member
        }
      },
      teamPersonnelPercentages: {
        ...prev.teamPersonnelPercentages,
        [trimmedName]: prev.personnelPercentage // 기본 인건비율 설정
      }
    }));
    
    // 입력 필드 초기화
    setTeamName('');
    setIsAddingTeam(false);
  };

  // 팀 삭제 함수
  const handleRemoveTeam = (teamNameToRemove: string) => {
    // 해당 팀에 속한 직원이 있는지 확인
    const teamHasEmployees = employees.some(emp => emp.team === teamNameToRemove);
    
    if (teamHasEmployees) {
      alert(`${teamNameToRemove}에 소속된 직원이 있어 삭제할 수 없습니다. 먼저 직원들을 다른 팀으로 이동시켜 주세요.`);
      return;
    }
    
    if (window.confirm(`${teamNameToRemove}을(를) 삭제하시겠습니까?`)) {
      // 로컬 상태에서 팀 제거
      setLocalTeams(prev => prev.filter(t => t !== teamNameToRemove));
      
      // AppContext에서 팀 제거
      setTeams(prev => Array.isArray(prev) 
        ? prev.filter(team => team && team.name !== teamNameToRemove) 
        : []);
      
      // 팀별 목표매출, 인센티브 비율, 인건비율에서도 제거
      const newTeamTargetSales = {...budgetData.teamTargetSales};
      delete newTeamTargetSales[teamNameToRemove];
      
      const newTeamIncentiveRatios = {...budgetData.teamIncentiveRatios};
      delete newTeamIncentiveRatios[teamNameToRemove];
      
      const newTeamPersonnelPercentages = {...budgetData.teamPersonnelPercentages};
      delete newTeamPersonnelPercentages[teamNameToRemove];
      
      setBudgetData(prev => ({
        ...prev,
        teamTargetSales: newTeamTargetSales,
        teamIncentiveRatios: newTeamIncentiveRatios,
        teamPersonnelPercentages: newTeamPersonnelPercentages
      }));
    }
  };

  // 팀별 목표매출 업데이트
  const handleUpdateTeamTargetSales = (teamName: string, value: string) => {
    const numValue = Number(value.replace(/,/g, '')) || 0;
    const currentYearMonth = format(currentDate, 'yyyy-MM');
    
    // 현재 월 데이터 업데이트
    setCurrentMonthData(prev => ({
      ...prev,
      teamTargetSales: {
        ...prev.teamTargetSales,
        [teamName]: numValue
      }
    }));
    
    // 현재 달이면 실제 목표매출도 업데이트
    const isCurrentMonth = format(new Date(), 'yyyy-MM') === currentYearMonth;
    
    if (isCurrentMonth) {
      setBudgetData(prev => {
        // 팀별 목표매출 업데이트
        const newTeamTargetSales = {
          ...prev.teamTargetSales,
          [teamName]: numValue
        };
        
        // 전체 목표매출 합계 계산
        const newTotalTargetSales = Object.values(newTeamTargetSales).reduce((sum: number, val) => sum + (Number(val) || 0), 0);
        
        // 팀별 인건비 계산
        // const teamEmployees = employees.filter(emp => emp.team === teamName); // Unused variable commented out
        // const teamSalaryTotal = teamEmployees.reduce((sum, emp) => 
        //   sum + (emp.salary || 0) + 
        //   (emp.retirement || 0) + 
        //   (emp.meal || 0) + 
        //   (emp.housing || 0) + 
        //   (emp.overtime || 0) + 
        //   (emp.individual || 0) + 
        //   (emp.other || 0), 0); // Unused variable commented out
        
        // 🔧 인건비율 자동 조정 기능 비활성화 - 기존 인건비율 유지
        
        return {
          ...prev,
          teamTargetSales: newTeamTargetSales,
          targetSales: newTotalTargetSales
          // teamPersonnelPercentages 업데이트 제거 - 기존 인건비율 유지
        };
      });
    }
  };

  // 목표매출 저장
  const handleSaveTargetSales = async () => {
    try {
      const currentYearMonth = format(currentDate, 'yyyy-MM');
      
      // 현재 월 데이터 업데이트
      const updatedMonthlyData = {
        ...budgetData.monthlyData,
        [currentYearMonth]: {
          ...(budgetData.monthlyData?.[currentYearMonth] || {}),
          teamTargetSales: currentMonthData.teamTargetSales,
          targetSales: Object.values(currentMonthData.teamTargetSales).reduce((sum: number, val) => sum + (Number(val) || 0), 0)
        }
      };
      
      // 현재 달이면 실제 목표매출도 업데이트
      const isCurrentMonth = format(new Date(), 'yyyy-MM') === currentYearMonth;
      
      setBudgetData(prev => ({
        ...prev,
        monthlyData: updatedMonthlyData,
        ...(isCurrentMonth ? {
          teamTargetSales: currentMonthData.teamTargetSales,
          targetSales: Object.values(currentMonthData.teamTargetSales).reduce((sum: number, val) => sum + (Number(val) || 0), 0)
        } : {})
      }));
      
      await saveTeamData();
      alert('팀 정보가 저장되었습니다.');
    } catch (error) {
      console.error('팀 정보 저장 실패:', error);
      alert('팀 정보 저장 중 오류가 발생했습니다.');
    }
  };

  // 총 기본급 계산
  const totalSalary = employees.reduce((sum, emp) => sum + (emp.salary || 0), 0);
  
  // 총 수당 계산
  const totalAllowances = employees.reduce((sum, emp) => 
    sum + 
    (emp.retirement || 0) + 
    (emp.meal || 0) + 
    (emp.housing || 0) + 
    (emp.overtime || 0) + 
    (emp.individual || 0) + 
    (emp.other || 0), 0);

  // 총 인건비 합계 (기본급 + 수당)
  const totalBaseSalaryAndAllowances = totalSalary + totalAllowances;
  
  // 팀별 목표매출 합계
  const totalTeamTargetSales = Object.values(currentMonthData.teamTargetSales || {}).reduce(
    (sum: number, val: any) => sum + (Number(val) || 0), 0
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-800 mb-4 md:mb-0">팀 관리</h1>
        <div className="flex items-center space-x-2">
          <button
            onClick={handlePreviousMonth}
            className="p-2 rounded-md hover:bg-gray-100 focus:outline-none"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-lg font-medium text-gray-700">
            {format(currentDate, 'yyyy년 MM월', { locale: ko })}
          </span>
          <button
            onClick={handleNextMonth}
            className="p-2 rounded-md hover:bg-gray-100 focus:outline-none"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* 팀 목록 */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-700">팀 목록</h2>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
            <select
              className="border border-gray-300 rounded p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={teamSortType}
              onChange={(e) => setTeamSortType(e.target.value as SortType)}
            >
              <option value="numeric">숫자순 정렬</option>
              <option value="alphabetic">알파벳순 정렬</option>
              <option value="korean">가나다순 정렬</option>
            </select>
            {!isAddingTeam ? (
              <button 
                className="bg-green-600 hover:bg-green-700 text-white py-1 px-3 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                onClick={() => setIsAddingTeam(true)}
              >
                팀 추가
              </button>
            ) : (
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="팀 이름 입력"
                  className="border border-gray-300 rounded p-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button 
                  className="bg-green-600 hover:bg-green-700 text-white py-1 px-3 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  onClick={handleAddTeam}
                >
                  추가
                </button>
                <button 
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 py-1 px-3 rounded text-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
                  onClick={() => {
                    setTeamName('');
                    setIsAddingTeam(false);
                  }}
                >
                  취소
                </button>
              </div>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {getSortedTeams(localTeams, teamSortType).map(team => {
            const teamEmployees = employees.filter(emp => emp.team === team);
            const teamLeader = teamEmployees.find(emp => emp.position === '팀장');
            const teamMemberCount = teamEmployees.filter(emp => emp.position === '팀원').length;
            
            return (
              <div key={team} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium text-gray-800">{team}</h3>
                  <button 
                    className="text-red-600 hover:text-red-800 text-sm"
                    onClick={() => handleRemoveTeam(team)}
                  >
                    삭제
                  </button>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <div>팀장: {teamLeader ? teamLeader.name : '없음'}</div>
                  <div>팀원: {teamMemberCount}명</div>
                  <div>인원: 총 {teamEmployees.length}명</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* 팀별 목표매출 */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-700">팀별 목표매출</h2>
          <button 
            className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            onClick={handleSaveTargetSales}
          >
            목표매출 저장
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">팀명</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">팀장</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">팀원 수</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">목표매출</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">인건비 합계</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">인건비율(%)</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {getSortedTeams(localTeams, teamSortType).map(team => {
                const teamEmployees = employees.filter(emp => emp.team === team);
                const teamLeader = teamEmployees.find(emp => emp.position === '팀장');
                const teamMemberCount = teamEmployees.filter(emp => emp.position === '팀원').length;
                const teamSalaryTotal = teamEmployees.reduce((sum, emp) => 
                  sum + (emp.salary || 0) + 
                  (emp.retirement || 0) + 
                  (emp.meal || 0) + 
                  (emp.housing || 0) + 
                  (emp.overtime || 0) + 
                  (emp.individual || 0) + 
                  (emp.other || 0), 0);
                const teamTargetSales = currentMonthData.teamTargetSales[team] || 0;
                const teamPersonnelPercentage = teamTargetSales > 0 ? (teamSalaryTotal / teamTargetSales) * 100 : 0;
                
                return (
                  <tr key={team} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{team}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{teamLeader ? teamLeader.name : '없음'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{teamMemberCount}명</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <input 
                        type="text" 
                        value={(currentMonthData.teamTargetSales[team] || 0).toLocaleString()} 
                        onChange={(e) => handleUpdateTeamTargetSales(team, e.target.value)}
                        className="border border-gray-300 p-1 rounded w-32 text-center"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{teamSalaryTotal.toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {teamPersonnelPercentage.toFixed(1)}%
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-gray-50 font-semibold">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">합계</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{employees.filter(emp => emp.position === '팀장').length}명</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{employees.filter(emp => emp.position === '팀원').length}명</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                  {totalTeamTargetSales.toLocaleString()}원
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">{totalBaseSalaryAndAllowances.toLocaleString()}원</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                  {totalTeamTargetSales > 0 ? ((totalBaseSalaryAndAllowances / totalTeamTargetSales) * 100).toFixed(1) : '0.0'}%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TeamsPage;